import { createPortal } from 'react-dom';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { FlowNodeData, FlowNodeOutput, PaletteNodeType } from '@repletoryreach/sdk-ui/dnd';
import { RAW_NODE_TYPE, TRIGGER_NODE_TYPE, isEngineNodeKind, type JsonObject } from '../mapper';
import { NODE_KINDS, PALETTE_ORDER, askOptions, llmMode, namedItems, outputsFor, intentEnabled } from './catalog';

/** `t` from `useT()`: key + optional vars, or key + fallback. */
export type Translate = (key: string, arg?: string | Record<string, string | number>) => string;
type NodeRenderer = (props: { node: FlowNodeData; selected: boolean }) => ReactNode;

const svg = (d: ReactNode, size = 16): ReactNode => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    {d}
  </svg>
);

/** Icons by the catalogue's icon name. */
export const ICONS: Record<string, ReactNode> = {
  bolt: svg(<path d="M9 1.5 3.5 9h4l-1 5.5L11.5 7h-4l1.5-5.5z" />),
  whatsapp: svg(<><path d="M2.5 13.5l1-3.2A5.8 5.8 0 1 1 5.7 12.4z" /><path d="M6 6.3c.2 1.9 1.8 3.5 3.7 3.7l.9-.9-1.3-.7-.6.5c-.7-.3-1.3-.9-1.6-1.6l.5-.6-.7-1.3z" /></>),
  webhook: svg(<><circle cx="4.5" cy="11" r="2" /><circle cx="11.5" cy="11" r="2" /><circle cx="8" cy="4.5" r="2" /><path d="M7 6.2 5.3 9.2M9 6.2l1.7 3M6.5 11h3" /></>),
  message: svg(<path d="M2 3.5h12v7H6.5L3 13v-2.5H2z" />),
  template: svg(<><path d="M4 2h5.5L13 5.5V14H4z" /><path d="M9.5 2v3.5H13" /><path d="M6 8.5h5M6 11h3.5" /></>),
  question: svg(<><path d="M2 3.5h12v7H6.5L3 13v-2.5H2z" /><path d="M6.6 6.1a1.5 1.5 0 0 1 2.9.5c0 .9-1.4 1.1-1.4 2" /><circle cx="8" cy="9.9" r=".4" fill="currentColor" /></>),
  sparkles: svg(<><path d="M8 1.5v2M8 12.5v2M1.5 8h2M12.5 8h2M3.4 3.4l1.4 1.4M11.2 11.2l1.4 1.4M3.4 12.6l1.4-1.4M11.2 4.8l1.4-1.4" /><circle cx="8" cy="8" r="2.5" /></>),
  globe: svg(<><circle cx="8" cy="8" r="6" /><path d="M2 8h12M8 2c2.2 2 2.2 10 0 12M8 2c-2.2 2-2.2 10 0 12" /></>),
  user: svg(<><circle cx="8" cy="5.2" r="2.6" /><path d="M2.8 14c.6-2.8 2.6-4.2 5.2-4.2s4.6 1.4 5.2 4.2" /></>),
  people: svg(<><circle cx="5" cy="5" r="2" /><circle cx="11" cy="5" r="2" /><path d="M1.5 13c0-2 1.5-3.5 3.5-3.5S8.5 11 8.5 13M7.5 13c0-2 1.5-3.5 3.5-3.5s3.5 1.5 3.5 3.5" /></>),
  braces: svg(<><path d="M6 2.5c-1.4 0-1.6.9-1.6 2.1s0 2.2-1.4 2.2v2.4c1.4 0 1.4 1 1.4 2.2s.2 2.1 1.6 2.1" /><path d="M10 2.5c1.4 0 1.6.9 1.6 2.1s0 2.2 1.4 2.2v2.4c-1.4 0-1.4 1-1.4 2.2s-.2 2.1-1.6 2.1" /></>),
  branch: svg(<><circle cx="8" cy="3" r="1.6" /><circle cx="3.5" cy="13" r="1.6" /><circle cx="12.5" cy="13" r="1.6" /><path d="M8 4.6v2.2c0 1-.6 1.6-1.6 2L4.2 9.9c-.5.2-.7.6-.7 1.1v.4M8 6.8c0 1 .6 1.6 1.6 2l2.2 1.1c.5.2.7.6.7 1.1v.4" /></>),
  decision: svg(<><path d="M8 1.8 13.2 7 8 12.2 2.8 7z" /><path d="M8 12.2v2.3M2.8 7H1.3M13.2 7h1.5" /></>),
  switch: svg(<><path d="M2.5 4.5h3l5 7h3" /><path d="M2.5 11.5h3l5-7h3" /><path d="M11.5 2.8l2 1.7-2 1.7M11.5 9.8l2 1.7-2 1.7" /></>),
  calendarCheck: svg(<><rect x="2.2" y="3.4" width="11.6" height="10.4" rx="2" /><path d="M2.2 6.6h11.6M5.4 1.9v2.6M10.6 1.9v2.6" /><path d="m5.6 10.1 1.6 1.5 3.2-3.1" /></>),
  calendarEdit: svg(<><rect x="2.2" y="3.4" width="11.6" height="10.4" rx="2" /><path d="M2.2 6.6h11.6M5.4 1.9v2.6M10.6 1.9v2.6" /><path d="M5.5 11.8h2M5.5 9.5h5" /></>),
  ticket: svg(<><path d="M1.8 4.2h12.4v2.3a1.5 1.5 0 0 0 0 3v2.3H1.8V9.5a1.5 1.5 0 0 0 0-3z" /><path d="M10 4.2v7.6" strokeDasharray="1.2 1.2" /></>),
  lookup: svg(<><ellipse cx="7" cy="3.6" rx="4.6" ry="1.8" /><path d="M2.4 3.6v6.2c0 1 2 1.8 4.6 1.8M11.6 3.6v3" /><circle cx="11.2" cy="10.6" r="2.2" /><path d="m12.8 12.2 1.6 1.6" /></>),
  // Change language (3 November 2026): a speech bubble with an "A" in front of another one.
  language: svg(<><path d="M9.6 6h4.6v5.6h-1.3v2l-2.5-2H6.6V9.8" /><path d="M1.8 2.2h7.8v6H5.2L2.9 10.1V8.2H1.8z" /><path d="m3.9 7 1.8-3.6L7.5 7M4.5 5.8h2.4" /></>),
  ticketSearch: svg(<><path d="M1.8 4.2h9.4M1.8 4.2v2.3a1.5 1.5 0 0 1 0 3v2.3h5.4" /><circle cx="11.3" cy="9.6" r="2.3" /><path d="m13 11.3 1.6 1.6" /></>),
  calendar: svg(<><rect x="2.2" y="3.4" width="11.6" height="10.4" rx="2" /><path d="M2.2 6.6h11.6M5.4 1.9v2.6M10.6 1.9v2.6" /><path d="M5.5 9.5h2M9 9.5h2M5.5 11.8h2" /></>),
  split: svg(<><path d="M8 13.5V8M8 8 3.5 3.5M8 8l4.5-4.5" /><path d="M3.5 6.5v-3h3M12.5 6.5v-3h-3" /></>),
  flow: svg(<><rect x="2" y="2" width="5" height="4" rx="1" /><rect x="9" y="10" width="5" height="4" rx="1" /><path d="M7 4h3a2 2 0 0 1 2 2v4" /></>),
  clipboard: svg(<><rect x="3" y="2.8" width="10" height="11.7" rx="1.6" /><path d="M6 1.8h4v2H6z" /><path d="M5.5 7.2h5M5.5 9.6h5M5.5 12h3" /></>),
  phone: svg(<><rect x="4.2" y="1.5" width="7.6" height="13" rx="1.6" /><path d="M7 12.3h2" /><path d="M6.3 5.2h3.4M6.3 7.6h2.4" /></>),
  mail: svg(<><rect x="1.8" y="3.3" width="12.4" height="9.4" rx="1.6" /><path d="m2.4 4.2 5.6 4.4 5.6-4.4" /></>),
  bell: svg(<><path d="M4 11V7.5a4 4 0 0 1 8 0V11l1.2 1.6H2.8z" /><path d="M6.5 13.6a1.5 1.5 0 0 0 3 0" /></>),
  clock: svg(<><circle cx="8" cy="8" r="6.2" /><polyline points="8,4.4 8,8 10.6,9.4" /></>),
  stop: svg(<><circle cx="8" cy="8" r="6" /><rect x="5.6" y="5.6" width="4.8" height="4.8" rx="1" fill="currentColor" stroke="none" /></>),
  generic: svg(<><rect x="2.5" y="2.5" width="11" height="11" rx="2" /><path d="M5.5 8h5" /></>),
  raw: svg(<><path d="M5 3 2 8l3 5M11 3l3 5-3 5" /></>)
};

const TRIGGER_TINT = '#16a34a';

/**
 * The catalogue as palette entries, grouped and titled from the catalogue.
 * `hidden` holds kinds that cannot be added right now (the SMS step while the
 * platform has no SMS); a flow that already has one still renders it.
 */
export function buildPalette(t: Translate, hidden: ReadonlySet<string> = new Set()): PaletteNodeType[] {
  const triggers: PaletteNodeType[] = [
    {
      type: TRIGGER_NODE_TYPE,
      label: t('flows.builder.node.trigger'),
      description: t('flows.builder.node.triggerHint'),
      icon: <span className="rr-flow-palette-icon" style={{ color: TRIGGER_TINT }}>{ICONS.whatsapp}</span>,
      group: t('flows.builder.group.triggers'),
      defaultData: { type: 'keyword', keyword: '' }
    }
  ];
  const rest = PALETTE_ORDER.filter((kind) => !hidden.has(kind)).map((kind) => {
    const spec = NODE_KINDS[kind];
    return {
      type: kind,
      label: t(`flows.builder.node.${kind}`),
      description: t(`flows.builder.node.${kind}Hint`),
      icon: <span className="rr-flow-palette-icon" style={{ color: spec.tint }}>{ICONS[spec.icon] ?? ICONS.generic}</span>,
      group: t(`flows.builder.group.${spec.group}`),
      defaultData: spec.defaults
    } satisfies PaletteNodeType;
  });
  return [...triggers, ...rest];
}

const dataOf = (node: FlowNodeData): JsonObject => (node.data ?? {}) as JsonObject;
const clip = (s: string, max = 56): string => (s.length > max ? `${s.slice(0, max - 1)}…` : s);

/**
 * The port label the customer-facing option carries, or the translated branch name. `kind` tells apart the
 * ports two steps name alike: `none` is "No bookings" on Manage booking and "Nothing open" on Ticket status.
 */
export function portLabel(port: FlowNodeOutput, t: Translate, kind?: string): string {
  if (kind === 'ticket_status' && port.id === 'none') return t('flows.builder.port.noTickets');
  if (port.id === 'not_found') return t('flows.builder.port.notFound');
  if (kind === 'ask' && port.id === 'chosen') return t('flows.builder.port.chosen');
  if (kind === 'language' && port.id === 'changed') return t('flows.builder.port.languageChanged');
  if (kind === 'language' && port.id === 'kept') return t('flows.builder.port.languageKept');
  if (kind === 'ask' && port.id === 'empty') return t('flows.builder.port.empty');
  switch (port.id) {
    case 'yes': return t('flows.builder.port.yes');
    case 'no': return t('flows.builder.port.no');
    case 'success': return t('flows.builder.port.success');
    case 'error': return t('flows.builder.port.error');
    case 'invalid': return t('flows.builder.port.invalid');
    case 'open': return t('flows.builder.port.open');
    case 'closed': return t('flows.builder.port.closed');
    case 'other': return t('flows.builder.port.other');
    case 'found': return t('flows.builder.port.found');
    case 'missing': return t('flows.builder.port.missing');
    case 'else': return t('flows.builder.port.else');
    case 'otherwise': return t('flows.builder.port.otherwise');
    case 'answered': return t('flows.builder.port.answered');
    case 'unsure': return t('flows.builder.port.unsure');
    case 'no_agent': return t('flows.builder.port.noAgent');
    case 'submitted': return t('flows.builder.port.submitted');
    case 'cancelled': return t('flows.builder.port.cancelled');
    case 'booked': return t('flows.builder.port.booked');
    case 'requested': return t('flows.builder.port.requested');
    case 'full': return t('flows.builder.port.full');
    case 'done': return t('flows.builder.port.done');
    case 'none': return t('flows.builder.port.noBookings');
    case 'too_late': return t('flows.builder.port.tooLate');
    case 'created': return t('flows.builder.port.created');
    case 'failed': return t('flows.builder.port.failed');
    default: return port.label ?? port.id;
  }
}

/** Output ports with translated labels, for the canvas. */
export function decorateOutputs(node: FlowNodeData, t: Translate): FlowNodeOutput[] {
  return outputsFor(node).map((p) => ({ id: p.id, label: clip(portLabel(p, t, node.type), 14) }));
}

/** Tint for a node type: the catalogue's, green for the trigger, grey for the unknown. */
export function tintOf(type: string): string {
  if (type === TRIGGER_NODE_TYPE) return TRIGGER_TINT;
  return NODE_KINDS[type]?.tint ?? '#64748b';
}

export function iconOf(type: string): ReactNode {
  if (type === TRIGGER_NODE_TYPE) return ICONS.whatsapp;
  if (type === RAW_NODE_TYPE) return ICONS.raw;
  const spec = NODE_KINDS[type];
  return spec ? (ICONS[spec.icon] ?? ICONS.generic) : ICONS.generic;
}

/** The kind's name: the card's title. An AI node says which mode it is in. */
/** "First contact (a new person)" → ["First contact", "a new person"]. */
function splitParen(text: string): [string, string] {
  const m = /^(.*?)\s*\((.*)\)\s*$/.exec(text);
  return m ? [m[1], m[2]] : [text, ''];
}

/** The kind's one-line hint, for the subtitle of a step that has no name of its own. */
export function hintOf(type: string, t: Translate): string {
  if (type === TRIGGER_NODE_TYPE) return t('flows.builder.node.triggerHint');
  return isEngineNodeKind(type) ? t(`flows.builder.node.${type === 'reply' ? 'send' : type}Hint`) : '';
}

export function kindTitle(type: string, t: Translate, data?: JsonObject): string {
  if (type === TRIGGER_NODE_TYPE) return t('flows.builder.node.trigger');
  if (type === RAW_NODE_TYPE) return t('flows.builder.node.raw');
  if (type === 'llm' && data) {
    const mode = llmMode(data);
    if (mode !== 'reply') return t(`flows.builder.llmMode.${mode}`);
  }
  return isEngineNodeKind(type) ? t(`flows.builder.node.${type === 'reply' ? 'send' : type}`) : type;
}

/** The node's own name, when the author gave it one. */
export function nodeName(node: FlowNodeData): string {
  const d = dataOf(node);
  return typeof d.name === 'string' ? d.name.trim() : '';
}

function triggerSummary(d: JsonObject, t: Translate): string {
  {
    const type = typeof d.type === 'string' ? d.type : '';
    if (type === 'first_contact') return t('flows.builder.trigger.firstContact');
    if (type === 'greeting') return t('flows.builder.trigger.greeting');
    if (type === 'default') return t('flows.builder.trigger.default');
    if (type === 'post_resolution') return t('flows.builder.trigger.postResolution');
  }
  if (d.type === 'always') return t('flows.builder.summary.always');
  if (d.type === 'webhook') return t('flows.builder.summary.webhook');
  const keywords = Array.isArray(d.keywords) ? d.keywords.map(String).filter(Boolean) : typeof d.keyword === 'string' && d.keyword ? [d.keyword] : [];
  if (d.type === 'message' && keywords.length === 0) return t('flows.builder.summary.anyMessage');
  const asked = intentEnabled(d);
  if (keywords.length) return `${t('flows.builder.summary.keyword')}: ${clip(keywords.map((k) => `“${k}”`).join(', '), 48)}${asked ? ` · ${t('flows.builder.summary.orAsked')}` : ''}`;
  return asked ? t('flows.builder.summary.asked') : t('flows.builder.summary.noKeyword');
}

/** One line of what the node does. */
export function summary(node: FlowNodeData, t: Translate): string {
  const d = dataOf(node);
  if (node.type === TRIGGER_NODE_TYPE) return triggerSummary(d, t);
  if (node.type === RAW_NODE_TYPE) return clip(JSON.stringify(d.__raw));
  const spec = NODE_KINDS[node.type];
  if (!spec) {
    const fields = Object.entries(d).filter(([k, v]) => k !== 'name' && k !== 'description' && (typeof v === 'string' || typeof v === 'number')).slice(0, 2);
    return fields.length ? fields.map(([k, v]) => `${k}: ${clip(String(v), 24)}`).join(' · ') : t('flows.builder.summary.noFields');
  }
  const s = spec.summary(d);
  if (!s) return '';
  return 'text' in s ? s.text : t(s.key, s.vars);
}

/** What the card's "…" menu and its name line can do to the node. */
export interface NodeActions {
  onDuplicate: (id: string) => void;
  onDelete: (id: string) => void;
  onRename: (id: string, name: string) => void;
  /** Keep the node, remove every link to and from it. */
  onDetach: (id: string) => void;
}

/**
 * A small menu on the card: the things people do to a node besides editing
 * it. The list renders through a portal at the top layer, positioned from
 * the button: inside the card it sat in the node's own stacking layer and
 * any node, port or label drawn after it covered it.
 */
function NodeMenu({ id, actions, t }: { id: string; actions: NodeActions; t: Translate }) {
  const [open, setOpen] = useState(false);
  const [at, setAt] = useState<{ top: number; right: number } | null>(null);
  const ref = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: PointerEvent) => {
      const target = e.target as Node;
      if (!ref.current?.contains(target) && !listRef.current?.contains(target)) setOpen(false);
    };
    // A pan, zoom or scroll moves the button away from the list; close rather than drift.
    const closeSilently = () => setOpen(false);
    window.addEventListener('pointerdown', close);
    window.addEventListener('scroll', closeSilently, true);
    window.addEventListener('resize', closeSilently);
    window.addEventListener('wheel', closeSilently, { passive: true });
    return () => {
      window.removeEventListener('pointerdown', close);
      window.removeEventListener('scroll', closeSilently, true);
      window.removeEventListener('resize', closeSilently);
      window.removeEventListener('wheel', closeSilently);
    };
  }, [open]);
  function toggle(e: React.MouseEvent) {
    e.stopPropagation();
    if (open) { setOpen(false); return; }
    const r = ref.current?.getBoundingClientRect();
    if (r) setAt({ top: r.bottom + 4, right: window.innerWidth - r.right });
    setOpen(true);
  }
  const stop = (e: React.SyntheticEvent) => e.stopPropagation();
  // Every pointer event stops here: a press on the menu must not drag or select the node.
  return (
    <div ref={ref} className="rr-flow-node-card__menu" onPointerDown={stop} onPointerUp={stop}>
      <button
        type="button"
        className="rr-flow-node-card__more"
        aria-label={t('flows.builder.nodeMenu')}
        aria-expanded={open}
        onClick={toggle}
      >
        <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden>
          <circle cx="3.2" cy="8" r="1.4" /><circle cx="8" cy="8" r="1.4" /><circle cx="12.8" cy="8" r="1.4" />
        </svg>
      </button>
      {open && at && createPortal(
        <div ref={listRef} className="rr-flow-node-card__dropdown" role="menu" style={{ top: at.top, right: at.right }} onPointerDown={stop} onPointerUp={stop}>
          <button type="button" role="menuitem" onClick={(e) => { e.stopPropagation(); setOpen(false); actions.onDuplicate(id); }}>
            {t('flows.builder.duplicate')}
          </button>
          <button type="button" role="menuitem" title={t('flows.builder.detachHint')} onClick={(e) => { e.stopPropagation(); setOpen(false); actions.onDetach(id); }}>
            {t('flows.builder.detach')}
          </button>
          <button type="button" role="menuitem" className="is-danger" onClick={(e) => { e.stopPropagation(); setOpen(false); actions.onDelete(id); }}>
            {t('action.delete')}
          </button>
        </div>,
        document.body
      )}
    </div>
  );
}

/**
 * The step's own name, editable in place: double-click to rename, Enter to
 * keep it, Escape to give up. Unnamed nodes show what they do instead.
 */
function NameLine({ id, name, fallback, actions, t }: { id: string; name: string; fallback: string; actions?: NodeActions; t: Translate }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(name);
  const inputRef = useRef<HTMLInputElement | null>(null);
  /*
   * The node captures the pointer on press, so the browser fires the
   * double-click on the node, not on this span. Two presses here within
   * 400 ms are the double-click; the second one is kept from the node so it
   * does not start a drag.
   */
  const lastDown = useRef(0);
  useEffect(() => { if (!editing) setDraft(name); }, [name, editing]);
  useEffect(() => { if (editing) inputRef.current?.select(); }, [editing]);

  if (editing && actions) {
    const commit = () => { setEditing(false); if (draft.trim() !== name) actions.onRename(id, draft.trim()); };
    return (
      <input
        ref={inputRef}
        className="rr-flow-node-card__rename"
        value={draft}
        placeholder={t('flows.builder.field.name')}
        aria-label={t('flows.builder.field.name')}
        autoFocus
        onChange={(e) => setDraft(e.currentTarget.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commit(); }
          if (e.key === 'Escape') { e.preventDefault(); setDraft(name); setEditing(false); }
          e.stopPropagation();
        }}
        onPointerDown={(e) => e.stopPropagation()}
        onPointerUp={(e) => e.stopPropagation()}
      />
    );
  }
  return (
    <span
      className={`rr-flow-node-card__name${name ? '' : ' is-fallback'}`}
      title={actions ? t('flows.builder.renameHint') : undefined}
      onPointerDown={(e) => {
        if (!actions || e.button !== 0) return;
        const now = performance.now();
        if (now - lastDown.current < 400) {
          e.stopPropagation();
          e.preventDefault();
          setEditing(true);
        }
        lastDown.current = now;
      }}
      onDoubleClick={(e) => { if (!actions) return; e.stopPropagation(); setEditing(true); }}
    >
      {name || fallback}
    </span>
  );
}

const StatusIcon = ({ status }: { status: 'ok' | 'warning' | 'error' | 'unlinked' }) => {
  if (status === 'ok') {
    return <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden><circle cx="8" cy="8" r="6.4" /><polyline points="5.2,8.2 7.2,10.2 10.9,6.2" /></svg>;
  }
  if (status === 'unlinked') {
    return <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden><circle cx="8" cy="8" r="6.4" /><path d="M5 8h6" /></svg>;
  }
  return <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M8 2.2 14.2 13H1.8z" /><path d="M8 6.4v3.2M8 11.6v.2" /></svg>;
};

/**
 * One renderer for every node type on the canvas, laid out as the reference
 * draws a step: the kind in bold, the author's own name for the step under
 * it (or what it does, when unnamed), a step number on the icon, a check when
 * it is fine, and a "…" menu.
 */
export function makeNodeRenderer(
  unlinked: Set<string>,
  problems: Map<string, 'warning' | 'error'>,
  t: Translate,
  actions?: NodeActions,
  stepOf?: Map<string, number>
): NodeRenderer {
  return function NodeCard({ node }) {
    const d = dataOf(node);
    const tint = tintOf(node.type);
    const name = nodeName(node);
    const known = node.type === TRIGGER_NODE_TYPE || isEngineNodeKind(node.type);
    const status = unlinked.has(node.id) ? 'unlinked' : problems.get(node.id) ?? 'ok';
    const statusText = status === 'unlinked'
      ? t('flows.builder.unlinked')
      : status === 'ok' ? t('flows.builder.status.ok') : t('flows.builder.status.check');
    const line = summary(node, t);
    const step = stepOf?.get(node.id);
    const chips = node.type === 'ask' || node.type === 'send'
      ? askOptions(d).map((o) => o.title)
      : node.type === 'llm' && llmMode(d) === 'classify' ? namedItems(d, 'labels').map((l) => l.title) : [];
    const isTrigger = node.type === TRIGGER_NODE_TYPE;
    // The trigger's summary reads "First contact (a new person)": the head is the title, the rest the subtitle.
    const [head, tail] = isTrigger ? splitParen(line) : [line, ''];
    const hint = hintOf(node.type, t);
    const subtitle = isTrigger ? tail || hint : name ? line || hint : hint;

    return (
      <div className="rr-flow-node-card" data-kind={node.type} data-status={status} style={{ ['--rr-node-tint' as string]: tint }}>
        <span className="rr-flow-node-card__icon" aria-hidden>{iconOf(node.type)}</span>
        <div className="rr-flow-node-card__body">
          <div className="rr-flow-node-card__kind">
            {step !== undefined && !isTrigger ? <span className="rr-flow-node-card__step" aria-label={t('flows.builder.stepNumber', { n: step })}>{step}</span> : null}
            <span>{kindTitle(node.type, t, d)}</span>
            {!known && <span className="rr-flow-node-card__tag">{t('flows.builder.passthrough')}</span>}
          </div>
          <NameLine id={node.id} name={name} fallback={head || kindTitle(node.type, t, d)} actions={isTrigger ? undefined : actions} t={t} />
          <div className="rr-flow-node-card__summary" title={subtitle}>
            {subtitle}
            {chips.length > 0 ? ` · ${chips.slice(0, 3).map((c) => clip(c, 14)).join(' · ')}${chips.length > 3 ? ` +${chips.length - 3}` : ''}` : ''}
          </div>
        </div>
        <div className="rr-flow-node-card__side">
          <span className={`rr-flow-node-card__status is-${status}`} title={statusText} aria-label={statusText}>
            <StatusIcon status={status} />
          </span>
          {actions && !isTrigger ? <NodeMenu id={node.id} actions={actions} t={t} /> : null}
        </div>
      </div>
    );
  };
}
